import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DripBuyToggleButton — May 2026.
 *
 * Two-way toggle for the DRIP flag on a position lot. Used for cases where
 * the DRIP checkbox was missed at buy-time, a CSV import didn't carry it,
 * or a position was incorrectly flagged.
 *
 * Visual states (both clickable):
 *   • Not DRIP yet → hollow blue water-drop (the icon the user uploaded).
 *     Click → "Would you like to make this a Drip Buy?" → Yes flips to solid.
 *   • Already DRIP → solid blue water-drop (the header brand mark).
 *     Click → "Remove this as a Drip Buy?" → Yes flips back to hollow.
 *
 * Sizing: the icon is intentionally a touch larger than the ticker text it
 * sits next to (currently rendered at text-xs / text-sm). The `w-4 h-4`
 * default works for both contexts; pass `size` to override.
 */
export default function DripBuyToggleButton({
  ticker,
  positionId,
  isDrip,
  onMarked,
  size = 'w-4 h-4',
  testIdSuffix = '',
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const action = isDrip ? 'unmark' : 'mark';
  const title = isDrip
    ? `Remove DRIP tag from this ${ticker} lot`
    : `Mark this ${ticker} lot as a DRIP buy`;
  const aria = isDrip
    ? `Remove DRIP tag from ${ticker} lot`
    : `Mark ${ticker} lot as DRIP buy`;
  const testId = isDrip
    ? `drip-marker-on-${testIdSuffix || ticker}`
    : `drip-marker-off-${testIdSuffix || ticker}`;
  const iconSrc = isDrip ? '/dividrip-solid.png' : '/dividrip-hollow.png';

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/portfolio/${encodeURIComponent(ticker)}/positions/${encodeURIComponent(positionId)}/${action}-drip`,
        {},
        { withCredentials: true },
      );
      if (data?.changed) {
        toast.success(
          isDrip ? 'Removed DRIP tag' : 'Marked as DRIP buy',
          {
            description: isDrip
              ? `${ticker} lot is no longer a DRIP purchase — its original cash source is restored.`
              : `${ticker} lot now counts as a dividend reinvestment. Cash Balance and Dividends Collected updated.`,
          },
        );
        window.dispatchEvent(new Event('cash-changed'));
      }
      setOpen(false);
      onMarked?.(data?.position);
    } catch (err) {
      toast.error(
        isDrip ? 'Could not remove DRIP tag' : 'Could not mark as DRIP',
        { description: err?.response?.data?.detail || err?.message },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center justify-center shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
        title={title}
        aria-label={aria}
        data-testid={testId}
      >
        <img
          src={iconSrc}
          alt=""
          className={`${size} block select-none`}
          draggable={false}
        />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="drip-confirm-modal"
        >
          <div
            className="bg-background border border-border/80 rounded-lg shadow-2xl w-full max-w-sm relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* X close — mirrors the StockModal close button pattern */}
            <button
              type="button"
              onClick={() => !busy && setOpen(false)}
              disabled={busy}
              className="absolute top-2.5 right-2.5 text-muted-foreground hover:text-foreground rounded p-1 disabled:opacity-40"
              aria-label="Close"
              data-testid="drip-confirm-close-x"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="px-5 pt-5 pb-2 flex items-center gap-3">
              <img
                src={iconSrc}
                alt=""
                className="w-7 h-7 shrink-0 select-none"
                draggable={false}
              />
              <div className="text-sm font-bold">
                {isDrip ? 'Remove this as a Drip Buy?' : 'Would you like to make this a Drip Buy?'}
              </div>
            </div>

            <div className="px-5 pb-2 text-[11px] text-muted-foreground">
              {ticker} ·{' '}
              {isDrip
                ? 'this lot goes back to a regular buy — the cash source it had before (inside or outside) is restored, so cash and contributions revert.'
                : 'this lot becomes a dividend reinvestment: the dividend paid for it, so no inside cash is drawn and it does not count as a contribution. It counts toward Dividends Collected.'}
            </div>

            <div className="px-5 pb-5 pt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => !busy && setOpen(false)}
                disabled={busy}
                className="px-3 py-1.5 rounded-md text-xs font-semibold border border-border hover:bg-muted disabled:opacity-50"
                data-testid="drip-confirm-no"
              >
                No
              </button>
              <button
                type="button"
                onClick={confirm}
                disabled={busy}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-white disabled:opacity-60 ${
                  isDrip ? 'bg-rose-600 hover:bg-rose-700' : 'bg-sky-600 hover:bg-sky-700'
                }`}
                data-testid="drip-confirm-yes"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {isDrip ? 'Yes, remove DRIP' : 'Yes, mark as DRIP'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
